import { ICONS } from 'consts/consts';
import { upperFirst } from 'lodash';
import React, { PropsWithChildren, useCallback, useMemo } from 'react';

import { type IntlShape, useIntl } from 'react-intl';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import { fullNameForPeopleFilter } from 'views/Widgets/Dashboards/TeamDashboard/helpers';
import Loading from 'views/Widgets/Loading';
import { getIconForFieldName } from 'views/Widgets/People/Filters/common';
import {
  getDescriptionForFieldName,
  getEnhancedDescriptionForFieldName,
} from 'views/Widgets/People/Table/commons';
import { ApiFilterItem, Filterable } from '../types';
import { ApiCondition, ApiConditionValue, ApiGroup } from './types';

export interface AdvancedFilterContextType {
  suggestions: Suggestion[] | undefined;
  selectedSuggestionConverter: (s: Filterable) => Filterable;
  filterToText: (group: ApiGroup | undefined) => string;
}

export type AdvancedFilterRoleScope =
  | 'SYSTEM_ADMIN'
  | 'HRBP'
  | 'USER'
  | 'ONE_ON_ONES_ADMIN'
  | 'CALIBRATION_VIEW_ADMINISTRATOR'
  | 'CALIBRATION_VIEW_EDITOR'
  | 'CUSTOM_SURVEY'
  | 'CUSTOM_SURVEY_AUDIENCE';
export type AdvancedFilterPopulationType =
  | 'EVERYONE'
  | 'ADMINABLE'
  | 'BELOW_CHAIN_OF_COMMAND'
  | 'BELOW_CHAIN_OF_COMMAND_OR_ADMINABLE';

const AdvancedCurrentPeopleFilterContext =
  React.createContext<AdvancedFilterContextType | null>(null);

type AdvancedFilterContextProviderProps = PropsWithChildren<{
  includeHierarchyFilters?: boolean;
  filterableFieldsDemographics?: string[];
  roleScope: AdvancedFilterRoleScope;
  population?: AdvancedFilterPopulationType;
  extras?: {
    surveyId?: number;
    // CUSTOM_SURVEY_AUDIENCE only: the survey's in-progress, not-yet-saved
    // target_filter (if the caller has one pending), so suggestions reflect
    // the audience the caller is about to save rather than the stale one
    // still in the DB - see available-filters-for-audience on the backend.
    targetFilterOverride?: Record<string, unknown> | null;
  };
}>;

// target_filter is an arbitrarily large FilterGroup tree with no size cap
// of its own - as a GET query param it can push the request line past
// gunicorn's default 4094-byte limit, so this one role scope takes it as a
// POST body instead (see available_filters_for_audience on the backend).
// Every other role scope has no such payload and stays GET.
const getMethodForRoleScope = (scope: AdvancedFilterRoleScope) => {
  switch (scope) {
    case 'CUSTOM_SURVEY_AUDIENCE':
      return 'POST';
    default:
      return 'GET';
  }
};

const AdvancedCurrentPeopleFilterContextProvider = ({
  includeHierarchyFilters = true,
  filterableFieldsDemographics,
  roleScope,
  population = 'EVERYONE',
  extras,
  children,
}: AdvancedFilterContextProviderProps) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { organization_id } = useProxyParameters();
  const [suggestions, setSuggestions] = React.useState<
    Suggestion[] | undefined
  >(undefined);
  const onSuccesfullFiltersRetrieve = useCallback(
    (data) => {
      // useConfirmApi also calls this on error, with data set to null
      if (!data) {
        return;
      }
      setSuggestions(
        data.available_filters.map((f) =>
          apiFilterItemToSuggestion(f, formatMessage)
        )
      );
    },
    [formatMessage]
  );

  const params = useMemo(
    () => ({
      include_hierarchy_filters: includeHierarchyFilters,
      filterable_fields_demographics: filterableFieldsDemographics
        ? filterableFieldsDemographics.join(',')
        : undefined,
      filterable_population: population,
      // Only meaningful for CUSTOM_SURVEY_AUDIENCE - see available-filters-for-audience.
      // Omitted (not just falsy) when the caller has no pending edit, so the
      // backend falls back to the survey's saved target_filter. Sent as-is
      // (not JSON.stringify'd) since this travels as a POST body - useConfirmApi
      // serializes the whole params object to JSON itself; stringifying here
      // too would double-encode it as a JSON string within the JSON body.
      target_filter:
        roleScope === 'CUSTOM_SURVEY_AUDIENCE'
          ? extras?.targetFilterOverride
          : undefined,
    }),
    [
      includeHierarchyFilters,
      filterableFieldsDemographics,
      population,
      roleScope,
      extras?.targetFilterOverride,
    ]
  );

  const url = useMemo(() => {
    switch (roleScope) {
      case 'SYSTEM_ADMIN':
        return `/organizations/${organization_id}/available-filters`;
      case 'USER':
        return `/organizations/${organization_id}/available-filters/for-users`;
      case 'ONE_ON_ONES_ADMIN':
        return `/organizations/${organization_id}/available-filters/for-one-on-ones-admin`;
      case 'CUSTOM_SURVEY':
        if (!extras?.surveyId)
          throw new Error('surveyId is required for CUSTOM_SURVEY scope');
        return `custom-survey/surveys/${extras.surveyId}/available-filters`;
      case 'CUSTOM_SURVEY_AUDIENCE':
        if (!extras?.surveyId)
          throw new Error(
            'surveyId is required for CUSTOM_SURVEY_AUDIENCE scope'
          );
        return `custom-survey/surveys/${extras.surveyId}/available-filters-for-audience`;
      default:
        throw new Error(`Unknown or non implemented role scope: ${roleScope}.`);
    }
  }, [organization_id, roleScope, extras?.surveyId]);

  useConfirmApi({
    url,
    method: getMethodForRoleScope(roleScope),
    params,
    clearOnChange: true,
    clearOnError: true,
    disabled: false,
    callback: onSuccesfullFiltersRetrieve,
  });

  const filterToText = useCallback(
    (group: ApiGroup | undefined): string => {
      if (suggestions) {
        return describeFilter(group, intl, suggestions);
      }
      return '';
    },
    [intl, suggestions]
  );

  const value: AdvancedFilterContextType = React.useMemo(
    () => ({
      suggestions,
      selectedSuggestionConverter,
      filterToText,
    }),
    [filterToText, suggestions]
  );

  return (
    <AdvancedCurrentPeopleFilterContext.Provider value={value}>
      {children}
    </AdvancedCurrentPeopleFilterContext.Provider>
  );
};

const useAdvancedCurrentPeopleFilter = (): AdvancedFilterContextType => {
  const context = React.useContext(AdvancedCurrentPeopleFilterContext);
  if (!context) {
    throw new Error(
      'useAdvancedCurrentPeopleFilter must be used within a AdvancedCurrentPeopleFilterContext'
    );
  }
  return context;
};

export interface AdvancedDatasetFilterContextType {
  suggestions: Suggestion[] | undefined;
  selectedSuggestionConverter: (s: Filterable) => Filterable;
  filterToText: (group: ApiGroup | undefined) => string;
}

const AdvancedDatasetFilterContext =
  React.createContext<AdvancedDatasetFilterContextType | null>(null);

const AdvancedDatasetFilterContextProvider = ({
  campaignId,
  includeHierarchyFilters = true,
  includeIndividualsFilters = false,
  filterableFieldsDemographics,
  roleScope,
  children,
  additionalData,
}: PropsWithChildren<{
  campaignId: number;
  includeHierarchyFilters?: boolean;
  includeIndividualsFilters?: boolean;
  filterableFieldsDemographics?: string[];
  roleScope: AdvancedFilterRoleScope;
  additionalData?: { [key: string]: string };
}>) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [suggestions, setSuggestions] = React.useState<
    Suggestion[] | undefined
  >(undefined);
  const onSuccesfullFiltersRetrieve = useCallback(
    (data) => {
      // useConfirmApi also calls this on error, with data set to null
      if (!data) {
        return;
      }
      setSuggestions(
        data.available_filters.map((f) =>
          apiFilterItemToSuggestion(f, formatMessage)
        )
      );
    },
    [formatMessage]
  );

  const params = useMemo(() => {
    return {
      ...additionalData,
      include_hierarchy_filters: includeHierarchyFilters,
      include_individuals_filters: includeIndividualsFilters,
      filterable_fields_demographics: filterableFieldsDemographics
        ? filterableFieldsDemographics.join(',')
        : undefined,
    };
  }, [
    includeHierarchyFilters,
    filterableFieldsDemographics,
    includeIndividualsFilters,
    additionalData,
  ]);

  const url = useMemo(() => {
    switch (roleScope) {
      case 'SYSTEM_ADMIN':
        return `/campaigns/${campaignId}/available-filters`;
      case 'HRBP':
        return `/campaigns/${campaignId}/available-filters/for-hrbp`;
      case 'CALIBRATION_VIEW_ADMINISTRATOR':
        return `/campaigns/${campaignId}/available-filters/for-calibration-view`;
      case 'CALIBRATION_VIEW_EDITOR':
        return `/campaigns/${campaignId}/available-filters/for-calibration-view-editor`;
      default:
        throw new Error(`Unknown or non implemented role scope: ${roleScope}.`);
    }
  }, [campaignId, roleScope]);

  useConfirmApi({
    url,
    method: 'GET',
    params,
    clearOnChange: true,
    clearOnError: true,
    disabled: false,
    callback: onSuccesfullFiltersRetrieve,
  });

  const filterToText = useCallback(
    (group: ApiGroup | undefined): string => {
      if (suggestions) {
        return describeFilter(group, intl, suggestions);
      }
      return '';
    },
    [intl, suggestions]
  );

  const value: AdvancedDatasetFilterContextType = React.useMemo(
    () => ({
      suggestions,
      selectedSuggestionConverter,
      filterToText,
    }),
    [filterToText, suggestions]
  );

  return (
    <AdvancedDatasetFilterContext.Provider value={value}>
      {children}
    </AdvancedDatasetFilterContext.Provider>
  );
};

const SAFE_FILTER_CONTEXT: AdvancedDatasetFilterContextType = {
  suggestions: undefined,
  selectedSuggestionConverter: () => {
    throw new Error('Not implemented');
  },
  filterToText: () => {
    throw new Error('Not implemented');
  },
};

const useAdvancedDatasetFilter = (
  safe?: boolean
): AdvancedDatasetFilterContextType => {
  const context = React.useContext(AdvancedDatasetFilterContext);
  if (!context) {
    if (safe) return SAFE_FILTER_CONTEXT;
    throw new Error(
      'useAdvancedDatasetFilter must be used within a AdvancedDatasetFilterContext'
    );
  }
  return context;
};

const AdvancedDatasetOrCurrentPeopleFilterContextProvider = ({
  campaignId,
  children,
  includeHierarchyFilters = true,
  filterableFieldsDemographics,
  roleScope,
}: PropsWithChildren<{
  campaignId: number;
  includeHierarchyFilters?: boolean;
  filterableFieldsDemographics?: string[];
  roleScope: AdvancedFilterRoleScope;
}>) => {
  const { formatMessage } = useIntl();

  const url = useMemo(() => {
    switch (roleScope) {
      case 'SYSTEM_ADMIN':
        return `/campaigns/${campaignId}/has-empty-dataset`;
      default:
        throw new Error(`Unknown or non implemented role scope: ${roleScope}.`);
    }
  }, [campaignId, roleScope]);

  const { data, error, status } = useConfirmApi<{ is_empty: boolean }>({
    url,
    method: 'GET',
  });

  if (status === 'LOADING') {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.widgets.advanced_specialized_filter.loading',
          defaultMessage: 'Loading filters information....',
        })}
      />
    );
  }

  if (error || !data) {
    return <SimpleErrorPage error={error} />;
  }

  if (data.is_empty) {
    return (
      <AdvancedCurrentPeopleFilterContextProvider
        includeHierarchyFilters={includeHierarchyFilters}
        filterableFieldsDemographics={filterableFieldsDemographics}
        roleScope={roleScope}
      >
        {children}
      </AdvancedCurrentPeopleFilterContextProvider>
    );
  }

  return (
    <AdvancedDatasetFilterContextProvider
      campaignId={campaignId}
      includeHierarchyFilters={includeHierarchyFilters}
      filterableFieldsDemographics={filterableFieldsDemographics}
      roleScope={roleScope}
    >
      {children}
    </AdvancedDatasetFilterContextProvider>
  );
};

const useAdvancedDatasetOrCurrentPeopleFilter =
  (): AdvancedFilterContextType => {
    const datasetContext = React.useContext(AdvancedDatasetFilterContext);
    const currentPeopleContext = React.useContext(
      AdvancedCurrentPeopleFilterContext
    );
    if (datasetContext) {
      return datasetContext;
    }
    if (currentPeopleContext) {
      return currentPeopleContext;
    }
    throw new Error(
      'useAdvancedDatasetOrCurrentPeopleFilter must be used within an AdvancedCurrentPeopleFilterContext or AdvancedDatasetFilterContext'
    );
  };

export interface PersonOrGroupSuggestion extends Filterable {
  _index?: 'person';
  isPerson?: true;
  name: string;
  object?: {
    avatar?: string;
    title?: string;
    full_name?: string;
    full_name_when_selected?: string;
  };
}

export interface OtherSuggestion extends Filterable {
  icon?: string;
  name: string;
  description: string;
}

export type Suggestion = OtherSuggestion | PersonOrGroupSuggestion;

// This is an adaptation since we have a wierd rule how to name level.
// The original implementation is in app/src/views/Widgets/People/Table/commons.ts
const adaptedDescritpionForFieldName = (
  filter: ApiFilterItem,
  formatMessage: IntlShape['formatMessage']
): string | undefined => {
  return getEnhancedDescriptionForFieldName(filter.key, formatMessage);
};

const apiFilterItemToSuggestion = (
  filter: ApiFilterItem,
  formatMessage: IntlShape['formatMessage']
): Suggestion => {
  if (
    filter.key === 'manager' ||
    filter.key === 'manager_or_above' ||
    filter.key === 'person' ||
    filter.key === 'final_rating_provided_by_person'
  ) {
    return {
      filterObject: {
        key: filter.key,
        value: filter.value,
        keyDescription:
          adaptedDescritpionForFieldName(filter, formatMessage) ??
          filter.key.replaceAll('_', ' '),
      },
      _index: 'person',
      isPerson: true,
      name: filter.descriptor?.full_name ?? '',
      object: {
        avatar: filter.descriptor?.avatar,
        title: filter.descriptor?.title ?? '',
        full_name: getSpecialisedPeopleFilter(filter, formatMessage),
        full_name_when_selected: filter.descriptor?.full_name,
      },
    };
  }

  return {
    icon: getIconForFilterKey(filter.key),
    name: filter.value,
    description: adaptedDescritpionForFieldName(filter, formatMessage),
    filterObject: {
      key: filter.key,
      value: filter.value,
      keyDescription:
        adaptedDescritpionForFieldName(filter, formatMessage) ??
        filter.key.replaceAll('_', ' '),
    },
  };
};

export const getIconForFilterKey = (key: string) => {
  if (key === 'manager' || key === 'manager_or_above') {
    return ICONS.PEOPLE;
  }

  return getIconForFieldName(key);
};

const getSpecialisedPeopleFilter = (
  filter: ApiFilterItem,
  formatMessage: IntlShape['formatMessage']
): string => {
  if (filter.key === 'person') {
    return filter.descriptor?.full_name ?? filter.value;
  } else if (filter.key === 'manager') {
    return formatMessage(
      {
        id: 'app.model.filters.manager',
        defaultMessage: "{name}'s direct reports",
      },
      { name: filter.descriptor?.full_name ?? filter.value }
    );
  } else if (filter.key === 'manager_or_above') {
    return formatMessage(
      {
        id: 'app.model.filters.manager_or_above',
        defaultMessage: "{name}'s full team",
      },
      { name: filter.descriptor?.full_name ?? filter.value }
    );
  }

  return filter.value;
};

export const describeFilter = (
  group: ApiGroup | undefined,
  intl: IntlShape,
  suggestions: Suggestion[]
): string => {
  if (!group) {
    return upperFirst(
      intl.formatMessage({
        id: 'app.model.filters.describe.everyone',
        defaultMessage: 'everyone',
      })
    );
  }

  return upperFirst(describeGroup(group, intl, suggestions));
};

const describeGroup = (
  group: ApiGroup,
  intl: IntlShape,
  suggestions: Suggestion[]
): string => {
  if (group.operator === 'AND') {
    return intl.formatMessage(
      {
        id: 'app.model.filters.describe.group.and',
        defaultMessage: 'match every condition: {list}',
      },
      {
        list: intl.formatList(
          group.values.map((s) =>
            s.type === 'CONDITION'
              ? describeCondition(s, intl, suggestions)
              : describeGroup(s, intl, suggestions)
          ),
          { type: 'conjunction' }
        ),
      }
    );
  }
  if (group.operator === 'OR') {
    return intl.formatMessage(
      {
        id: 'app.model.filters.describe.group.or',
        defaultMessage: 'match at least one condition: {list}',
      },
      {
        list: intl.formatList(
          group.values.map((s) =>
            s.type === 'CONDITION'
              ? describeCondition(s, intl, suggestions)
              : describeGroup(s, intl, suggestions)
          ),
          { type: 'disjunction' }
        ),
      }
    );
  }
  throw new Error('Unknown operator');
};

const describeCondition = (
  condition: ApiCondition,
  intl: IntlShape,
  suggestions: Suggestion[]
): string => {
  const operator = condition.operator;
  return intl.formatMessage(
    {
      id: 'app.model.filters.describe.condition',
      defaultMessage:
        '{key} {operator, select, EQ { is } NE { is not} GT { is greater than } GTE { is at least } LT { is less than } LTE { is at most } other {} } {values}',
    },
    {
      operator: operator,
      key:
        getDescriptionForFieldName(condition.key!, intl.formatMessage) ??
        condition.key?.replaceAll('_', ' '),
      values: intl.formatList(
        condition.values.map((v) => descriptionForValueValue(v, suggestions)),
        { type: operator === 'NE' ? 'conjunction' : 'disjunction' }
      ),
    }
  );
};

const descriptionForValueValue = (
  value: ApiConditionValue,
  suggestions: Suggestion[]
): string => {
  if (
    value.key === 'person' ||
    value.key === 'manager' ||
    value.key === 'manager_or_above' ||
    value.key === 'final_rating_provided_by_person'
  ) {
    const suggestion = suggestions.find(
      (s) =>
        s.filterObject?.key === value.key &&
        s.filterObject?.value === value.value
    );
    if (suggestion) {
      return (
        (suggestion as PersonOrGroupSuggestion).object
          ?.full_name_when_selected ?? ''
      );
    }
  }
  return value.value;
};

export {
  AdvancedCurrentPeopleFilterContextProvider,
  AdvancedDatasetFilterContextProvider,
  AdvancedDatasetOrCurrentPeopleFilterContextProvider,
  useAdvancedCurrentPeopleFilter,
  useAdvancedDatasetFilter,
  useAdvancedDatasetOrCurrentPeopleFilter,
};

export const selectedSuggestionConverter = (s: Filterable): Filterable => {
  if (
    s.filterObject.key === 'manager' ||
    s.filterObject.key === 'manager_or_above' ||
    s.filterObject.key === 'final_rating_provided_by_person'
  ) {
    return {
      ...s,
      // @ts-expect-error
      object: {
        // @ts-expect-error
        ...s.object,
        // @ts-expect-error
        full_name: s.object.full_name_when_selected,
      },
    };
  }
  return s;
};

export const filterablePeopleTableSuggestionConverter = (
  s: Filterable
): Filterable => {
  if (
    s.filterObject.key === 'manager' ||
    s.filterObject.key === 'manager_or_above' ||
    s.filterObject.key === 'final_rating_provided_by_person' ||
    // @ts-expect-error
    s.isPerson
  ) {
    return {
      ...s,
      // @ts-expect-error
      object: {
        // @ts-expect-error
        ...s.object,
        // @ts-expect-error
        full_name: s.object.full_name_when_selected,
      },
    };
  }
  return s;
};

export function suggestionForSimpleProp(
  fieldName: any,
  fieldValue: any,
  col: any,
  iconName: any,
  formatMessage: IntlShape['formatMessage']
) {
  return {
    name:
      typeof col.getFilterDisplayValue === 'function'
        ? col.getFilterDisplayValue(fieldValue)
        : fieldValue,
    icon: iconName,
    filterObject: {
      key: fieldName,
      value: fieldValue,
      keyDescription:
        col.suggestionConditionName ??
        col.name ??
        getDescriptionForFieldName(fieldName, formatMessage),
    },
  };
}

export function suggestionForPerson(
  fieldName: any,
  fieldValue: any,
  col: any,
  formatMessage: IntlShape['formatMessage']
) {
  return {
    name: fieldValue.full_name,
    icon: undefined,
    filterObject: {
      key: fieldName,
      value: fieldValue.id,
      keyDescription:
        col.suggestionConditionName ??
        col.name ??
        getDescriptionForFieldName(fieldName, formatMessage),
    },
    // Stuff that helps rendering the tags
    _index: 'person',
    isPerson: true,
    id: fieldValue.id,
    object: {
      avatar: fieldValue.avatar,
      title: fieldValue.title || '',
      full_name: col.nameTransformerFunction
        ? col.nameTransformerFunction(fieldValue.full_name)
        : fullNameForPeopleFilter(fieldName, fieldValue, formatMessage),
      email: fieldValue.email,
      full_name_when_selected: fieldValue.full_name,
    },
  };
}
